import {
    GmDtoLibraryDtoTransportAssignmentTransportOrderDto,
    GmDtoLibraryDtoTransportAssignmentTransportShipmentDto, TransportAssignmentApi
} from "~/gen/openapi/sblService";
import {MS_HOUR, MS_MINUTE} from "~/constants/time";
import {useAxiosClient} from "~/use/axios/client";
import {keepPreviousData, useMutation, useQuery, UseQueryOptions} from "@tanstack/vue-query";
import {notNil} from "~/helpers/notNil";
import {queryClient} from "~/plugins/vue-query";
import {extractErrorMessagesFromErrorOrResponse} from "~/helpers/errors";

export const useTransportService = () => {
    const { axiosClient } = useAxiosClient()
    const transportApi = new TransportAssignmentApi(undefined, '', axiosClient.value)

    const defaultQueryOptions = {
        staleTime: 10 * MS_MINUTE,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        gcTime: MS_HOUR
    } as UseQueryOptions<any>

    const parkedTransportOrdersQueryOptions = {
        queryKey: ['transportAssignmentGetParkedOrdersList'],
        queryFn: async () => {
            try {
                return (await transportApi.transportAssignmentGetParkedOrdersList())?.data?.FollowUpListModel || []
            } catch (error) {
                throw new Error(extractErrorMessagesFromErrorOrResponse(error)[0])
            }
        },
        ...defaultQueryOptions
    }

    const parkedTransportOrdersQuery = () => useQuery(parkedTransportOrdersQueryOptions)

    const parkedTransportShipmentDetailsByVsysIdQueryOptions = {
        queryKey: ['transportAssignmentGetReadyToReactivateShipmentList'],
        queryFn: async () => {
            try {
                const contractVsysIds = (await queryClient.ensureQueryData(parkedTransportOrdersQueryOptions)).map((order) => order.HeadId).filter(notNil)
                const promises = []
                const parkedTransportShipmentDetailsByVsysId: Record<string, GmDtoLibraryDtoTransportAssignmentTransportShipmentDto[]> = {}
                for (const contractVsysId of contractVsysIds) {
                    promises.push(transportApi.transportAssignmentGetReadyToReactivateShipmentList(contractVsysId+'').then((response) => {
                        parkedTransportShipmentDetailsByVsysId[contractVsysId] = response.data || []
                    }))
                }
                await Promise.all(promises)
                return parkedTransportShipmentDetailsByVsysId as Record<string, GmDtoLibraryDtoTransportAssignmentTransportShipmentDto[]>
            } catch (error) {
                throw new Error(extractErrorMessagesFromErrorOrResponse(error)[0])
            }
        },
        ...defaultQueryOptions
    }

    const parkedTransportShipmentDetailsByVsysIdQuery = () => useQuery(parkedTransportShipmentDetailsByVsysIdQueryOptions)

    const reactivateShipmentMutation = () => useMutation({
        mutationFn: async (shipmentId: string) => {
            return await transportApi.transportAssignmentReactivateShipmentAction(shipmentId)
        },
        onSuccess: (_, variables) => {
            // @ts-ignore
            queryClient.setQueryData(parkedTransportShipmentDetailsByVsysIdQueryOptions.queryKey, (oldData: Record<string, GmDtoLibraryDtoTransportAssignmentTransportShipmentDto[]>) => {
                return Object.fromEntries(
                    Object.entries(oldData).map(([vsysId, shipments]) => {
                        //@ts-ignore
                        return [vsysId, shipments.filter((shipment) => shipment.ShipmentEventId !== variables.shipmentId)]
                    })
                )
            })
        },
        onError: (error) => {
            throw new Error(extractErrorMessagesFromErrorOrResponse(error)[0])
        }
    })

    const reactivateOrderMutation = () => useMutation({
        mutationFn: async (orderId: string) => {
            return await transportApi.transportAssignmentReactivateOrderAction(orderId)
        },
        onSuccess: (_, variables) => {
            //@ts-ignore
            queryClient.setQueryData(parkedTransportOrdersQueryOptions.queryKey, (oldData: GmDtoLibraryDtoTransportAssignmentTransportOrderDto[]) => {
                //@ts-ignore
                return [...oldData].filter((order) => order.HeadId !== variables.orderId) as GmDtoLibraryDtoTransportAssignmentTransportOrderDto[]
            })
        },
        onError: (error) => {
            throw new Error(extractErrorMessagesFromErrorOrResponse(error)[0])
        }
    })

    return {
        parkedTransportOrdersQueryOptions,
        parkedTransportOrdersQuery,
        parkedTransportShipmentDetailsByVsysIdQueryOptions,
        parkedTransportShipmentDetailsByVsysIdQuery,
        reactivateShipmentMutation,
        reactivateOrderMutation,
    }
}